import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { formatCurrency } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Search, Package, FileSpreadsheet, Plus, Minus, Check, X, AlertTriangle,
  Building2, RefreshCw, Trash2
} from 'lucide-react';

const SayimSection = ({
  warehouses,
  products: productsFromProps,
  sayimWarehouse,
  setSayimWarehouse,
  sayimSearch,
  setSayimSearch,
  sayimData,
  setSayimData,
  sayimQuickAdd,
  setSayimQuickAdd
}) => {
  const searchInputRef = useRef(null);
  const quickAddInputRef = useRef(null);
  const [lastAction, setLastAction] = useState(null);
  const [quickAddQty, setQuickAddQty] = useState(1);

  // Use products from props
  const products = productsFromProps || [];

  // Focus search input on warehouse change
  useEffect(() => {
    if (sayimWarehouse && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [sayimWarehouse]);

  // Advanced search - same logic as user dashboard
  const filteredProducts = useMemo(() => {
    if (!sayimWarehouse) return [];
    
    return products.filter(product => {
      const searchLower = sayimSearch.toLowerCase().trim();
      const searchTokens = searchLower
        .replace(/[\/\-_,\.]/g, ' ')
        .split(/\s+/)
        .filter(t => t.length > 0);
      
      const productText = [
        product.name,
        product.code,
        product.brand,
        product.season,
        product.dot
      ].join(' ').toLowerCase().replace(/[\/\-_,\.]/g, ' ');
      
      const matchesSearch = searchTokens.length === 0 || 
        searchTokens.every(token => productText.includes(token));
      
      return matchesSearch;
    });
  }, [sayimSearch, sayimWarehouse, products]);

  // Sort products - recently counted first
  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      const aData = sayimData[a.id];
      const bData = sayimData[b.id];
      
      // If both have timestamp, sort by most recent
      if (aData?.timestamp && bData?.timestamp) {
        return bData.timestamp - aData.timestamp;
      }
      // If only one has timestamp, it goes first
      if (aData?.timestamp) return -1;
      if (bData?.timestamp) return 1;
      
      return 0;
    });
  }, [filteredProducts, sayimData]);

  // Get stock for selected warehouse
  const getWarehouseStock = useCallback((product) => {
    return product.stock[sayimWarehouse] || 0;
  }, [sayimWarehouse]);

  // Get counted amount
  const getCountedAmount = useCallback((productId) => {
    return sayimData[productId]?.counted || 0;
  }, [sayimData]);

  // Update count
  const updateCount = useCallback((productId, amount) => {
    setSayimData(prev => ({
      ...prev,
      [productId]: {
        counted: Math.max(0, amount),
        timestamp: Date.now()
      }
    }));
    setLastAction({ productId, action: 'update', timestamp: Date.now() });
  }, [setSayimData]);

  // Increment count
  const incrementCount = useCallback((productId, increment = 1) => {
    const current = sayimData[productId]?.counted || 0;
    updateCount(productId, current + increment);
  }, [sayimData, updateCount]);

  // Decrement count
  const decrementCount = useCallback((productId) => {
    const current = sayimData[productId]?.counted || 0;
    updateCount(productId, current - 1);
  }, [sayimData, updateCount]);

  // Quick add by code
  const handleQuickAdd = useCallback((e) => {
    e.preventDefault();
    if (!sayimQuickAdd.trim()) return;

    const searchLower = sayimQuickAdd.toLowerCase().trim();
    const searchTokens = searchLower
      .replace(/[\/\-_,\.]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length > 0);

    // Find product matching the search
    const product = products.find(p => {
      const productText = [p.name, p.code, p.brand].join(' ').toLowerCase().replace(/[\/\-_,\.]/g, ' ');
      return searchTokens.every(token => productText.includes(token));
    });

    if (product) {
      incrementCount(product.id, quickAddQty);
      setSayimQuickAdd('');
      setQuickAddQty(1);
      quickAddInputRef.current?.focus();
    } else {
      alert('Ürün bulunamadı!');
    }
  }, [sayimQuickAdd, quickAddQty, incrementCount, setSayimQuickAdd]);

  // Get status
  const getStatus = useCallback((product) => {
    const stock = getWarehouseStock(product);
    const counted = getCountedAmount(product.id);
    
    if (counted === 0) return { status: 'pending', label: 'Sayılmadı', className: 'bg-gray-100 text-gray-600' };
    if (counted === stock) return { status: 'complete', label: 'Tam', className: 'bg-green-100 text-green-800' };
    if (counted < stock) return { status: 'missing', label: 'Eksik', className: 'bg-red-100 text-red-800' };
    return { status: 'excess', label: 'Fazla', className: 'bg-yellow-100 text-yellow-800' };
  }, [getWarehouseStock, getCountedAmount]);

  // Export to Excel (CSV format)
  const exportToExcel = useCallback(() => {
    const warehouseName = warehouses.find(w => w.id === sayimWarehouse)?.name || 'Depo';
    const date = new Date().toLocaleDateString('tr-TR');
    
    let csv = '\ufeff'; // BOM for UTF-8
    csv += `Sayım Raporu - ${warehouseName} - ${date}\n\n`;
    csv += 'Ürün Kodu;Ürün Adı;Marka;Sistem Stok;Sayılan;Fark;Durum\n';
    
    sortedProducts.forEach(product => {
      const stock = getWarehouseStock(product);
      const counted = getCountedAmount(product.id);
      const diff = counted - stock;
      const status = getStatus(product);
      
      csv += `${product.code};${product.name};${product.brand};${stock};${counted};${diff};${status.label}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sayim_${warehouseName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [sortedProducts, sayimWarehouse, warehouses, getWarehouseStock, getCountedAmount, getStatus]);

  // Reset sayım
  const resetSayim = useCallback(() => {
    if (window.confirm('Tüm sayım verileri silinecek. Emin misiniz?')) {
      setSayimData({});
    }
  }, [setSayimData]);

  // Stats
  const stats = useMemo(() => {
    const total = sortedProducts.length;
    const counted = sortedProducts.filter(p => sayimData[p.id]?.counted > 0).length;
    const complete = sortedProducts.filter(p => {
      const stock = getWarehouseStock(p);
      const count = getCountedAmount(p.id);
      return count > 0 && count === stock;
    }).length;
    const missing = sortedProducts.filter(p => {
      const stock = getWarehouseStock(p);
      const count = getCountedAmount(p.id);
      return count > 0 && count < stock;
    }).length;

    return { total, counted, complete, missing };
  }, [sortedProducts, sayimData, getWarehouseStock, getCountedAmount]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Sayım</h2>
        <div className="flex flex-wrap gap-2">
          <Select value={sayimWarehouse} onValueChange={setSayimWarehouse}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Depo Seçin" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map(wh => (
                <SelectItem key={wh.id} value={wh.id}>
                  <div className="flex items-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    {wh.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {sayimWarehouse && (
            <>
              <Button variant="outline" onClick={resetSayim}>
                <RefreshCw className="w-4 h-4 mr-2" /> Sıfırla
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={exportToExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel İndir
              </Button>
            </>
          )}
        </div>
      </div>

      {!sayimWarehouse ? (
        <Alert className="bg-blue-50 border-blue-200">
          <Building2 className="w-4 h-4" />
          <AlertDescription className="text-blue-800">
            Sayıma başlamak için yukarıdan bir depo seçin.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-sm text-gray-500">Toplam Ürün</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.counted}</p>
                <p className="text-sm text-gray-500">Sayılan</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.complete}</p>
                <p className="text-sm text-gray-500">Tam</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.missing}</p>
                <p className="text-sm text-gray-500">Eksik</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Add */}
          <Card>
            <CardContent className="p-4">
              <form onSubmit={handleQuickAdd} className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    ref={quickAddInputRef}
                    placeholder="Ürün kodu veya isim..."
                    value={sayimQuickAdd}
                    onChange={(e) => setSayimQuickAdd(e.target.value)}
                    className="pl-10 text-lg h-12"
                    autoComplete="off"
                  />
                </div>
                <Input
                  type="number"
                  min="1"
                  value={quickAddQty}
                  onChange={(e) => setQuickAddQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24 text-center text-lg h-12 font-bold"
                  placeholder="Adet"
                />
                <Button type="submit" className="h-12 px-6 bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-5 h-5 mr-2" /> Ekle
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Search Filter */}
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  ref={searchInputRef}
                  placeholder="Ürün ara: 205/55, michelin primacy, R16..."
                  value={sayimSearch}
                  onChange={(e) => setSayimSearch(e.target.value)}
                  className="pl-10"
                  autoComplete="off"
                />
              </div>
            </CardContent>
          </Card>

          {/* Products Table */}
          <Card>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-16">Resim</TableHead>
                      <TableHead>Ürün</TableHead>
                      <TableHead className="w-24 text-center">Sistem</TableHead>
                      <TableHead className="w-48 text-center">Sayım</TableHead>
                      <TableHead className="w-24 text-center">Fark</TableHead>
                      <TableHead className="w-24 text-center">Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProducts.map(product => {
                      const stock = getWarehouseStock(product);
                      const counted = getCountedAmount(product.id);
                      const diff = counted - stock;
                      const status = getStatus(product);
                      const isRecent = sayimData[product.id]?.timestamp && 
                        (Date.now() - sayimData[product.id].timestamp) < 3000;

                      return (
                        <TableRow 
                          key={product.id} 
                          className={`transition-all duration-300 ${isRecent ? 'bg-blue-50' : ''}`}
                        >
                          <TableCell>
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-12 h-12 object-contain bg-gray-100 rounded"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/48?text=Lastik';
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-mono text-xs text-gray-500">{product.code}</p>
                              <p className="font-medium text-sm">{product.name}</p>
                              <p className="text-xs text-gray-500">{product.brand}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-lg font-bold text-gray-700">{stock}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10"
                                onClick={() => decrementCount(product.id)}
                                disabled={counted === 0}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                type="number"
                                value={counted}
                                onChange={(e) => updateCount(product.id, parseInt(e.target.value) || 0)}
                                className="w-20 text-center text-lg font-bold h-10"
                                min="0"
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10"
                                onClick={() => incrementCount(product.id)}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-10"
                                onClick={() => updateCount(product.id, stock)}
                                title="Sistem stokuna eşitle"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`text-lg font-bold ${diff > 0 ? 'text-yellow-600' : diff < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {diff > 0 ? `+${diff}` : diff}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={status.className}>
                              {status.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {sortedProducts.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Ürün bulunamadı.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SayimSection;
